// Adapted from https://github.com/facebook/lexical/blob/v0.16.1/packages/lexical-playground/src/plugins/PillsPlugin/index.tsx

/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { test as fuzzyTest } from "fuzzy";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
	LexicalTypeaheadMenuPlugin,
	MenuOption,
	useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { type ElementNode, type LexicalNode, $isElementNode, type TextNode } from "lexical";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as ReactDOM from "react-dom";
import { $createPillNode } from "./PillNode";
import type { Pill } from "./common";

// At most, 15 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 15;

function usePillLookupService<T extends string = string>(pills: Array<Pill<T>>, pillString: string | null) {
	const [results, setResults] = useState<Array<Pill<T>>>([]);
	useEffect(() => {
		if (pillString == null) {
			setResults([]);
			return;
		}
		setResults(pills.filter((p) => fuzzyTest(pillString, p.label)));
	}, [pillString, pills]);

	return results;
}

class PillTypeaheadOption<T extends string = string> extends MenuOption {
	constructor(public pill: Pill<T>) {
		super(pill.label);
	}
}

function PillsTypeaheadMenuItem<T extends string = string>({
	index,
	isSelected,
	onClick,
	onMouseEnter,
	option,
}: {
	index: number;
	isSelected: boolean;
	onClick: () => void;
	onMouseEnter: () => void;
	option: PillTypeaheadOption<T>;
}) {
	return (
		<li
			key={option.key}
			tabIndex={-1}
			className={toClassName({
				[`selected bg-[var(--palette-P50)]`]: isSelected,
				[`item px-4 py-2 hover:bg-[var(--palette-P50)]`]: true,
				[`text-[#80848B] cursor-default`]: option.pill.disaled,
			})}
			ref={option.setRefElement}
			role="option"
			aria-selected={isSelected}
			id={"typeahead-item-" + index}
			onMouseEnter={option.pill.disaled ? noop : onMouseEnter}
			onClick={option.pill.disaled ? noop : onClick}
		>
			{option.pill.label}
		</li>
	);
}

export function $getAllNodes(root: ElementNode): Array<LexicalNode> {
	const nodes = [];
	let child: LexicalNode | null = root.getFirstChild();
	while (child !== null) {
		nodes.push(child);
		if ($isElementNode(child)) {
			const subChildrenNodes = $getAllNodes(child);
			nodes.push(...subChildrenNodes);
		}
		child = child.getNextSibling();
	}
	return nodes;
}

export default function PillsPlugin<T extends string = string>(props: {
	pills: Array<Pill<T>>;
	onClick?: (pill: Pill<T>) => void;
	onSelect?: (pill: Pill<T>) => void;
}): React.JSX.Element | null {
	const [editor] = useLexicalComposerContext();

	const [queryString, setQueryString] = useState<string | null>(null);

	const results = usePillLookupService(props.pills, queryString);

	const checkForTriggerMatch = useBasicTypeaheadTriggerMatch("#", {
		minLength: 0,
	});

	const options = useMemo(
		() => results.map((result) => new PillTypeaheadOption(result)).slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
		[results],
	);

	const onSelectOption = useCallback(
		(selectedOption: PillTypeaheadOption<T>, nodeToReplace: TextNode | null, closeMenu: () => void) => {
			if (selectedOption.pill.disaled) {
				return;
			}
			props.onSelect?.(selectedOption.pill);
			editor.update(() => {
				const pillNode = $createPillNode(selectedOption.pill, () => props.onClick?.(selectedOption.pill));
				if (nodeToReplace) {
					nodeToReplace.replace(pillNode);
				}
				pillNode.select();
				closeMenu();
			});
		},
		[editor, props],
	);

	const checkForPillMatch = useCallback(
		(text: string) => {
			return checkForTriggerMatch(text, editor);
		},
		[checkForTriggerMatch, editor],
	);

	return (
		<LexicalTypeaheadMenuPlugin<PillTypeaheadOption<T>>
			onQueryChange={setQueryString}
			onSelectOption={onSelectOption}
			triggerFn={checkForPillMatch}
			options={options}
			menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
				anchorElementRef.current && results.length
					? ReactDOM.createPortal(
							<div className="typeahead-popover pills-menu drop-shadow cursor-pointer min-w-[196px] bg-white">
								<ul>
									{options.map((option, i: number) => (
										<PillsTypeaheadMenuItem
											index={i}
											isSelected={selectedIndex === i}
											onClick={() => {
												setHighlightedIndex(i);
												selectOptionAndCleanUp(option);
											}}
											onMouseEnter={() => {
												setHighlightedIndex(i);
											}}
											key={option.key}
											option={option}
										/>
									))}
								</ul>
							</div>,
							anchorElementRef.current,
						)
					: null
			}
		/>
	);
}
